import { CloseOutlined } from '@ant-design/icons'
import { Image, StyleSheet, Text, View } from '@react-pdf/renderer'
import {
  AutoComplete,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Radio,
  Select,
  Space,
} from 'antd'
import { CantileverButton } from 'components/CantileverButton'
import dayjs from 'dayjs'
import { PageFooter } from 'features/reporting/pdfGeneration/components/shared/PageFooter'
import { PageHeader } from 'features/reporting/pdfGeneration/components/shared/PageHeader'
import { ReportPage } from 'features/reporting/pdfGeneration/components/shared/ReportPage'
import { PDF_COLORS } from 'features/reporting/pdfGeneration/constants/colors'
import { PdfProps, ReportSection } from 'features/reporting/types'
import { useRolodexEntries } from 'features/rolodex'
import chunk from 'lodash/chunk'
import { X } from 'lucide-react'
import { AddendumPages } from 'pages/reporting/reports/rug/components/AddendumPages'
import {
  METHOD_TO_PROCEDURE,
  METHOD_TO_PROCEDURE_TITLE,
} from 'pages/reporting/reports/rug/constants/inspectionInfo'
import {
  DEFAULT_CALIBRATION_STEPS,
  DEFAULT_PAYLOADS,
} from 'pages/reporting/reports/rug/hooks/useCantileverReportData'
import { useComponentNominalRangeString } from 'pages/reporting/reports/rug/hooks/useComponentNominalRangeString'
import {
  findDacDataForRobotSerial,
  getDacDataByRobotSerial,
  normalizeSalesforceRobotModel,
} from 'pages/reporting/reports/rug/hooks/useSalesforceReportData'
import toka5Image from 'pages/reporting/reports/rug/images/toka-5.png'
import {
  CalibrationBlock,
  DacData,
  DataSheetSectionData,
  RobotData,
  RugReportSectionId,
  SensorSerialNumbers,
} from 'pages/reporting/reports/rug/rugReportData'
import { useRugReportOps } from 'pages/reporting/reports/rug/RugReportProvider'
import {
  CalibrationBlockType,
  CalibrationStepName,
  ReportInspectionSurface,
  ReportSurfaceCondition,
  SensorPayloadType,
} from 'pages/reporting/reports/rug/types'
import { getSensorPayloadStrings } from 'pages/reporting/reports/rug/utils/getSensorPayloadStrings'
import { formatInspectionTemperature } from 'pages/reporting/reports/rug/utils/inspectionTemperature'
import { MeasurementInput } from 'pages/reporting/shared/components/MeasurementInput'
import { SourceIndicator } from 'pages/reporting/shared/components/SourceIndicator'
import { ASSET_DIMENSION_COMPONENT_TYPES } from 'pages/reporting/shared/constants'
import { StyledForm } from 'pages/reporting/shared/StyledComponents'
import React, { useMemo } from 'react'
import { rUGInspectionRobotInfoSchema } from 'rolodex-ontology-lib-ts'
import styled from 'styled-components'
import {
  GeckoAssetModel,
  GeckoComponentModel,
  GeckoComponentModelType,
  GeckoCone,
  GeckoHoodBend,
  GeckoHoodStraight,
  GeckoLegacyBoilerWalls,
  GeckoLegacyCone,
  GeckoMeasurement,
  GeckoMeasurementUnit,
  GeckoPipeEccentricReducer,
  GeckoPipeElbow,
  GeckoPipeTJoint,
  GeckoTankFlatRoof,
  GeckoTankFloor,
  GeckoVerticalTankFoundation,
  RolodexEntryType,
  TimeString,
} from 'types'
import {
  autocompleteFilterOptionWithoutExactMatch,
  autocompletePreviewDisabledProps,
} from 'utils/fde/antD'
import { joinUniqueDisplayValues } from 'utils/stringHelpers'

interface PayloadSerialNumbers {
  serialNumbers: string[]
}

const toPayloadSerialNumberRows = (
  sensorSerialNumbers: SensorSerialNumbers | undefined
): PayloadSerialNumbers[] => {
  if (!sensorSerialNumbers) {
    return []
  }

  return Object.keys(sensorSerialNumbers)
    .sort((a, b) => Number(a) - Number(b))
    .map((key) => ({
      serialNumbers: sensorSerialNumbers[Number(key)] || [],
    }))
}

const DISTANCE_UNIT_OPTIONS = [
  {
    value: GeckoMeasurementUnit.Inches.id,
    label: GeckoMeasurementUnit.Inches.name,
  },
  {
    value: GeckoMeasurementUnit.Millimeters.id,
    label: GeckoMeasurementUnit.Millimeters.name,
  },
]

const CALIBRATION_STEP_OPTIONS = Object.values(CalibrationStepName).map(
  (name) => ({ value: name, label: name })
)

const ROBOT_MODEL_OPTIONS = (
  rUGInspectionRobotInfoSchema._def.right.shape.robotType._def
    .options as Array<{
    _def: { value: string }
  }>
).map((option) => ({
  value: option._def.value,
}))

const INSPECTION_SOFTWARE_OPTIONS = [
  { value: 'Fulcrum' },
  { value: 'Robot Control' },
]

const DEFAULT_SERIAL_NUMBERS_COUNT = 24

const autocompleteStyle = {
  width: '100%',
}

const SerialNumberLabel = styled.span`
  display: flex;
  align-items: center;
  padding-right: 0.5rem;
  font-weight: 500;
  min-width: 2.5rem;
`

const PayloadGroupWrapper = styled.div`
  margin-bottom: 1rem;
`

const PayloadCard = styled(Space)`
  width: 100%;
  border: 1px solid #d9d9d9;
  border-radius: 0.375rem;
  padding: 1rem;
  position: relative;
`

const PayloadCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
`

const PayloadCardTitle = styled.span`
  font-weight: 500;
`

const RemoveButton = styled(CloseOutlined)`
  cursor: pointer;
`

const RemoveButtonWithPadding = styled(CloseOutlined)`
  padding: 0 0.5rem;
  cursor: pointer;
`

const ActionButton = styled(CantileverButton)`
  width: 100%;
`

const InlineFormRow = styled(Space.Compact)`
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
  width: 100%;
`

const CalibrationTimeRow = styled(Space)`
  display: flex;
  margin-bottom: 0.5rem;
`

const InlineFormItem = styled(Form.Item)`
  flex: 1;
  min-width: 0;
  margin-bottom: 0;
`

const CompactFormItem = styled(Form.Item)`
  margin-bottom: 0.5rem;
`

const FlushFormItem = styled(Form.Item)`
  margin-bottom: 0;
`

const PayloadFrequencyInput = styled(InputNumber)`
  width: 100%;
`

const CalibrationBlockSerialInput = styled(Input)`
  width: 9.375rem;
`

const DistanceValueInput = styled(InputNumber)`
  width: 6.25rem;
`

const UnitSelect = styled(Select)`
  width: 5rem;
`

const RemoveIcon = styled(X)`
  cursor: pointer;
  margin-left: 0.5rem;
  color: #999;
  flex-shrink: 0;
`

const VerticalStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const RobotDacFields = styled(VerticalStack)`
  flex: 2;
  min-width: 0;
`

const RobotDacInput = styled(Input)`
  flex: 1;
  min-width: 0;
  width: 0;
`

const RobotDacDatePicker = styled(DatePicker)`
  flex: 1;
  min-width: 0;
  width: 0;
`

const InlineFieldGroup = styled(Space.Compact)`
  width: 100%;
`

const ParserModalDescription = styled.div`
  margin-bottom: 1rem;
`

const ParserTextArea = styled(Input.TextArea)`
  font-family: monospace;
`

const DataSheetStyles = StyleSheet.create({
  section: {
    marginBottom: 8,
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 'ultrabold',
    textDecoration: 'underline',
    color: PDF_COLORS.TEXT_PRIMARY,
    marginBottom: 5,
  },
  twoColumnLayout: {
    flexDirection: 'row',
    marginBottom: 8,
  },
  column: {
    flexBasis: 0,
    flexGrow: 1,
  },
  separator: {
    width: 1,
    backgroundColor: 'black',
    marginHorizontal: 8,
  },
  fieldRow: {
    fontSize: 9,
    marginBottom: 3,
    flexDirection: 'row',
  },
  fieldLabel: {
    fontWeight: 'bold',
  },
  fieldValue: {
    color: PDF_COLORS.TEXT_PRIMARY,
  },
  tableHeader: {
    fontSize: 9,
    flexBasis: 0,
    flexGrow: 1,
    marginHorizontal: 8,
    fontWeight: 'bold',
  },
  tableHeaderAccent: {
    fontSize: 9,
    flexBasis: 0,
    flexGrow: 1,
    marginHorizontal: 8,
    fontWeight: 'bold',
    color: PDF_COLORS.TEXT_PRIMARY,
  },
  tableCell: {
    fontSize: 9,
    flexBasis: 0,
    flexGrow: 1,
    marginHorizontal: 20,
  },
  tableCellAccent: {
    fontSize: 9,
    flexBasis: 0,
    flexGrow: 1,
    marginHorizontal: 20,
    color: PDF_COLORS.TEXT_SECONDARY,
  },
  tableContainer: {
    flexDirection: 'row',
  },
  tableColumn: {
    flexBasis: 0,
    flexGrow: 1,
    borderTop: 1,
    flexDirection: 'column',
  },
  tableColumnWithBorder: {
    flexBasis: 0,
    flexGrow: 1,
    borderTop: 1,
    borderRight: 1,
    flexDirection: 'column',
  },
  tableHeaderRow: {
    flexBasis: 'auto',
    flexGrow: 1,
    borderBottom: 1,
  },
  tableDataRow: {
    flexDirection: 'row',
  },
  payloadContainer: {
    marginBottom: 12,
  },
  payloadContainerLast: {
    marginBottom: 0,
  },
  payloadTitle: {
    fontSize: 11,
    fontWeight: 'ultrabold',
    color: PDF_COLORS.TEXT_PRIMARY,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
  },
  calibrationHeader: {
    flexDirection: 'row',
    marginBottom: 3,
  },
  calibrationColumn: {
    fontSize: 9,
    flexBasis: 0,
    flexGrow: 1,
  },
  calibrationHeaderColumn: {
    fontSize: 9,
    fontWeight: 'bold',
    flexBasis: 0,
    flexGrow: 1,
  },
  calibrationTitle: {
    fontSize: 11,
    fontWeight: 'ultrabold',
    textDecoration: 'underline',
    color: PDF_COLORS.TEXT_PRIMARY,
    flexBasis: 0,
    flexGrow: 1,
  },
  calibrationValue: {
    color: PDF_COLORS.TEXT_SECONDARY,
  },
})

const DataSheetTitle: React.FC<{ children?: React.ReactNode }> = ({
  children,
}) => <Text style={DataSheetStyles.sectionTitle}>{children}</Text>

const DataSheetSeparator: React.FC = () => (
  <View style={DataSheetStyles.separator} />
)

const DataSheetListedItem: React.FC<{
  fieldName: string
  fieldValue?: string
}> = ({ fieldName, fieldValue }) => (
  <View style={DataSheetStyles.fieldRow}>
    <Text style={DataSheetStyles.fieldLabel}>{fieldName}</Text>
    <Text> {fieldValue ?? 'N/A'}</Text>
  </View>
)

export const getAssetDimensionsString = (
  assetModel: GeckoAssetModel | undefined,
  componentModels: readonly GeckoComponentModel[],
  formatMeasurement: (params: {
    measurement: GeckoMeasurement | undefined
    minimumFractionDigits?: number
    maximumFractionDigits?: number
  }) => string | undefined
): string => {
  if (!assetModel || componentModels.length === 0) {
    return 'N/A'
  }

  const preferredComponentType =
    ASSET_DIMENSION_COMPONENT_TYPES[assetModel.data.type]
  const dimensionComponent =
    componentModels.find(
      (component) => component.type === preferredComponentType
    ) ?? componentModels[0]
  const componentType = dimensionComponent.type

  const formatDiameterAndLength = (
    radius: GeckoMeasurement | undefined,
    lengthOrHeight: GeckoMeasurement | undefined,
    lengthLabel: 'Height' | 'Length'
  ) => {
    if (!radius || !lengthOrHeight) {
      return undefined
    }

    const diameter = GeckoMeasurement.CreateFromValue(
      radius.num * 2,
      radius.unit
    )
    return `Diameter: ${formatMeasurement({
      measurement: diameter,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })}  |  ${lengthLabel}: ${formatMeasurement({
      measurement: lengthOrHeight,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })}`
  }

  const formatDiameter = (radius: GeckoMeasurement | undefined) => {
    if (!radius) return undefined

    return formatMeasurement({
      measurement: GeckoMeasurement.CreateFromValue(
        radius.num * 2,
        radius.unit
      ),
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })
  }

  const formatTubeDimensions = (
    component: GeckoLegacyBoilerWalls | GeckoHoodStraight
  ) => {
    if (!component.tubeDiameter || !component.tubeSpacing) {
      return undefined
    }

    // Inches selects millimeters for metric reports instead of meters.
    return `Tube Diameter: ${formatMeasurement({
      measurement: component.tubeDiameter.convertTo(
        GeckoMeasurementUnit.Inches
      ),
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}  |  Tube C2C: ${formatMeasurement({
      measurement: component.tubeSpacing.convertTo(GeckoMeasurementUnit.Inches),
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`
  }

  const component = dimensionComponent as GeckoComponentModel & {
    radius?: GeckoMeasurement
    height?: GeckoMeasurement
    length?: GeckoMeasurement
  }

  switch (componentType) {
    case GeckoComponentModelType.VerticalTankShell:
      return (
        formatDiameterAndLength(component.radius, component.height, 'Height') ??
        'N/A'
      )
    case GeckoComponentModelType.HorizontalTankShell:
    case GeckoComponentModelType.PipeSection:
      return (
        formatDiameterAndLength(component.radius, component.length, 'Length') ??
        'N/A'
      )
    case GeckoComponentModelType.TankFloor:
    case GeckoComponentModelType.TankFlatRoof: {
      const tankSurface = dimensionComponent as
        | GeckoTankFloor
        | GeckoTankFlatRoof
      const diameter = formatDiameter(tankSurface.radius)
      return diameter ? `Diameter: ${diameter}` : 'N/A'
    }
    case GeckoComponentModelType.LegacyCone:
    case GeckoComponentModelType.Cone: {
      const cone = dimensionComponent as GeckoLegacyCone | GeckoCone
      return (
        formatDiameterAndLength(
          cone.outerRadius,
          cone.componentBounds.height,
          'Height'
        ) ?? 'N/A'
      )
    }
    case GeckoComponentModelType.VerticalTankFoundation: {
      const foundation = dimensionComponent as GeckoVerticalTankFoundation
      return (
        formatDiameterAndLength(
          foundation.radius,
          foundation.height,
          'Height'
        ) ?? 'N/A'
      )
    }
    case GeckoComponentModelType.PipeElbow: {
      const elbow = dimensionComponent as GeckoPipeElbow
      const diameter = formatDiameter(elbow.radius)
      if (!diameter) return 'N/A'

      return `Diameter: ${diameter}  |  Major Radius: ${formatMeasurement({
        measurement: elbow.majorRadius,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      })}  |  Angle: ${elbow.angle} deg`
    }
    case GeckoComponentModelType.PipeTJoint: {
      const joint = dimensionComponent as GeckoPipeTJoint
      const braceDiameter = formatDiameter(joint.brace_radius)
      const chordDiameter = formatDiameter(joint.chord_radius)
      if (!braceDiameter || !chordDiameter) return 'N/A'

      return `Brace Diameter: ${braceDiameter}  |  Chord Diameter: ${chordDiameter}  |  Length: ${formatMeasurement(
        {
          measurement: joint.chord_length,
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        }
      )}`
    }
    case GeckoComponentModelType.PipeEccentricReducer: {
      const reducer = dimensionComponent as GeckoPipeEccentricReducer
      const inletDiameter = formatDiameter(reducer.inlet_radius)
      const outletDiameter = formatDiameter(reducer.outlet_radius)
      if (!inletDiameter || !outletDiameter) return 'N/A'

      return `Inlet Diameter: ${inletDiameter}  |  Outlet Diameter: ${outletDiameter}  |  Length: ${formatMeasurement(
        {
          measurement: reducer.length,
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        }
      )}`
    }
    case GeckoComponentModelType.LegacyBoilerWalls:
      return (
        formatTubeDimensions(dimensionComponent as GeckoLegacyBoilerWalls) ??
        'N/A'
      )
    case GeckoComponentModelType.HoodStraight:
      return (
        formatTubeDimensions(dimensionComponent as GeckoHoodStraight) ?? 'N/A'
      )
    case GeckoComponentModelType.HoodBend: {
      const hoodBend = dimensionComponent as GeckoHoodBend
      return `Major Radius: ${formatMeasurement({
        measurement: hoodBend.majorRadius,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      })}  |  Minor Radius: ${formatMeasurement({
        measurement: hoodBend.minorRadius,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      })}  |  Torus Angle: ${hoodBend.torusAngle} deg`
    }
    default: {
      const lengthOrHeight = component.height || component.length
      const lengthLabel = component.height ? 'Height' : 'Length'
      return (
        formatDiameterAndLength(
          component.radius,
          lengthOrHeight,
          lengthLabel
        ) ?? 'N/A'
      )
    }
  }
}

const formatUniqueRobotModels = (
  models: readonly (string | undefined)[]
): string | undefined =>
  joinUniqueDisplayValues(
    models.map((model) => normalizeSalesforceRobotModel(model))
  )

const RenderPDF: React.FC<PdfProps> = ({ onRender }) => {
  const {
    formData,
    selectedAssetModel,
    selectedComponentModels,
    componentToNominalRange,
    inspectionMetadata,
    formatMeasurement,
  } = useRugReportOps()

  const mainInfo = useMemo(() => formData.mainInfo, [formData.mainInfo])
  const dataSheetData = useMemo(
    () => formData.dataSheetSectionData,
    [formData.dataSheetSectionData]
  )
  const inspectionTemperature = useMemo(
    () => formatInspectionTemperature(mainInfo.inspectionTemperature),
    [mainInfo.inspectionTemperature]
  )

  const nominalRangeStr = useComponentNominalRangeString(
    componentToNominalRange
  )

  const dimensionsStr = useMemo(
    () =>
      getAssetDimensionsString(
        selectedAssetModel,
        selectedComponentModels || [],
        formatMeasurement
      ),
    [selectedAssetModel, selectedComponentModels, formatMeasurement]
  )

  const { payloadTypesStr, sensorFrequenciesStr, sensorDiametersStr } =
    getSensorPayloadStrings({
      payloads: dataSheetData?.payloads,
      formatMeasurement,
    })

  const sensorAngle = inspectionMetadata.sensorPayloadMetadata?.angle

  const softwareVersion =
    dataSheetData?.inspectionSoftware ??
    inspectionMetadata.inspectionSoftwareVersion

  const sensorPayloadSerialNumbers = dataSheetData?.sensorSerialNumbers
    ? Object.entries(dataSheetData.sensorSerialNumbers)
        .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
        .map((item) => item[1])
    : []

  const calibrationSteps = useMemo(() => {
    return (
      dataSheetData?.calibrationSteps?.map((step) => {
        return {
          stepName: step.stepName,
          distanceMeasurement: formatMeasurement({
            measurement: GeckoMeasurement.CreateFromValue(
              step.distance.value,
              GeckoMeasurementUnit.GetOrCreate(step.distance.unit)
            ),
            variant: 'narrow',
            minimumFractionDigits: 3,
            maximumFractionDigits: 3,
          }),
        }
      }) ?? []
    )
  }, [dataSheetData?.calibrationSteps, formatMeasurement])

  return (
    <>
      <ReportPage
        tracker={{
          sectionId: RugReportSectionId.DATA_SHEET,
          onRender,
          registerOnce: false,
        }}
        header={<PageHeader title='Inspection Data Sheet' />}
        footer={
          <PageFooter
            jobNumber={mainInfo.jobNumber}
            revisionNumber={formData.revisionMetadata.revisionNumber}
          />
        }
        contentStyle={{
          flexGrow: 0,
          marginTop: 0,
          marginBottom: 0,
          marginHorizontal: 0,
        }}>
        <View style={DataSheetStyles.section}>
          <DataSheetTitle>Asset Information</DataSheetTitle>

          <View style={DataSheetStyles.twoColumnLayout}>
            <View style={DataSheetStyles.column}>
              <DataSheetListedItem
                fieldName='Asset:'
                fieldValue={mainInfo.assetType}
              />
              <DataSheetListedItem
                fieldName='Material:'
                fieldValue={mainInfo.assetMaterial}
              />
              <DataSheetListedItem
                fieldName='Nominal:'
                fieldValue={nominalRangeStr}
              />
              <DataSheetListedItem
                fieldName='Dimensions:'
                fieldValue={dimensionsStr}
              />
            </View>
            <DataSheetSeparator />
            <View style={DataSheetStyles.column}>
              <DataSheetListedItem
                fieldName='Inspection Surface:'
                fieldValue={dataSheetData?.inspectionSurface}
              />
              <DataSheetListedItem
                fieldName='Surface Condition:'
                fieldValue={dataSheetData?.surfaceCondition}
              />
              <DataSheetListedItem
                fieldName='Coating:'
                fieldValue={
                  dataSheetData?.coatingPresent === true
                    ? 'Yes'
                    : dataSheetData?.coatingPresent === false
                      ? 'No'
                      : undefined
                }
              />
              <DataSheetListedItem
                fieldName='Temperature:'
                fieldValue={inspectionTemperature}
              />
            </View>
          </View>
        </View>

        <View style={DataSheetStyles.section}>
          <DataSheetTitle>Inspection Procedure</DataSheetTitle>
          <DataSheetListedItem
            fieldName='Procedure:'
            fieldValue={dataSheetData?.procedure}
          />
          <DataSheetListedItem
            fieldName='Procedure Title:'
            fieldValue={dataSheetData?.procedureTitle}
          />
        </View>

        <View style={DataSheetStyles.twoColumnLayout}>
          <View style={DataSheetStyles.column}>
            <View style={DataSheetStyles.section}>
              <DataSheetTitle>Inspection Equipment</DataSheetTitle>
              <DataSheetListedItem
                fieldName='Number of Robots:'
                fieldValue={dataSheetData?.robots?.length.toString()}
              />
              <DataSheetListedItem
                fieldName='Robot/DAC Model(s):'
                fieldValue={formatUniqueRobotModels(
                  dataSheetData?.robots?.map((r) => r.model) ?? []
                )}
              />
              <View style={DataSheetStyles.calibrationHeader}>
                <Text style={DataSheetStyles.calibrationHeaderColumn}>
                  Robot/DAC S/N
                </Text>
                <Text style={DataSheetStyles.calibrationHeaderColumn}>
                  DAC S/N
                </Text>
                <Text style={DataSheetStyles.calibrationHeaderColumn}>
                  DAC Calibration Due Date
                </Text>
              </View>
              {dataSheetData?.robots?.flatMap((robot, index) =>
                (robot.dacs ?? []).map((dac, dacIndex) => (
                  <View
                    key={`${robot.serialNumber}-${index}-${dacIndex}`}
                    style={DataSheetStyles.calibrationHeader}>
                    <Text style={DataSheetStyles.calibrationColumn}>
                      {robot.serialNumber}
                    </Text>
                    <Text style={DataSheetStyles.calibrationColumn}>
                      {dac.serialNumber}
                    </Text>
                    <Text style={DataSheetStyles.calibrationColumn}>
                      {dac.calibrationDate
                        ? dayjs(dac.calibrationDate).format('MM/DD/YYYY')
                        : ''}
                    </Text>
                  </View>
                ))
              )}
            </View>

            <View style={DataSheetStyles.section}>
              <DataSheetTitle>Calibration Standard</DataSheetTitle>
              <DataSheetListedItem
                fieldName='Type:'
                fieldValue={joinUniqueDisplayValues(
                  dataSheetData?.calibrationBlocks?.map((block) => block.type)
                )}
              />
              <DataSheetListedItem
                fieldName='Material:'
                fieldValue={mainInfo.assetMaterial}
              />
              <DataSheetListedItem
                fieldName='S/N:'
                fieldValue={dataSheetData?.calibrationBlocks
                  ?.map((b) => b.serialNumber)
                  .filter(Boolean)
                  .join(', ')}
              />
            </View>

            <View style={DataSheetStyles.section}>
              <DataSheetTitle>Calibration Verification</DataSheetTitle>
              {dataSheetData?.calibrationTimes &&
                dataSheetData.calibrationTimes.length > 0 && (
                  <>
                    <View style={DataSheetStyles.calibrationHeader}>
                      <Text style={DataSheetStyles.calibrationHeaderColumn}>
                        Date
                      </Text>
                      <Text style={DataSheetStyles.calibrationHeaderColumn}>
                        Time
                      </Text>
                    </View>
                    {dataSheetData.calibrationTimes.map(
                      (calibrationTime, idx) => {
                        const timeValue = dayjs.isDayjs(calibrationTime)
                          ? calibrationTime
                          : dayjs(calibrationTime)
                        return (
                          <View
                            key={idx}
                            style={DataSheetStyles.calibrationHeader}>
                            <Text
                              style={[
                                DataSheetStyles.calibrationColumn,
                                DataSheetStyles.calibrationValue,
                              ]}>
                              {timeValue.format('MM/DD/YYYY')}
                            </Text>
                            <Text
                              style={[
                                DataSheetStyles.calibrationColumn,
                                DataSheetStyles.calibrationValue,
                              ]}>
                              {timeValue.format('HH:mm a')}
                            </Text>
                          </View>
                        )
                      }
                    )}
                  </>
                )}
            </View>
          </View>

          <DataSheetSeparator />

          <View style={DataSheetStyles.column}>
            <View style={DataSheetStyles.section}>
              <DataSheetListedItem
                fieldName='Inspection Software:'
                fieldValue={softwareVersion}
              />
              <DataSheetListedItem
                fieldName='Analysis Software:'
                fieldValue='UT Scope'
              />
              <DataSheetListedItem
                fieldName='Measurement Mode:'
                fieldValue={inspectionMetadata.measurementModeLabel}
              />
            </View>

            <View style={DataSheetStyles.section}>
              <View style={DataSheetStyles.calibrationHeader}>
                <Text style={DataSheetStyles.calibrationTitle}>
                  Calibration
                </Text>
                <Text style={DataSheetStyles.calibrationHeaderColumn}>
                  Distance
                </Text>
                <Text style={DataSheetStyles.calibrationHeaderColumn}>
                  Amplitude
                </Text>
              </View>
              {calibrationSteps.map((step, index) => {
                return (
                  <View key={index} style={DataSheetStyles.calibrationHeader}>
                    <Text style={DataSheetStyles.calibrationColumn}>
                      {step.stepName} Step:
                    </Text>
                    <Text
                      style={[
                        DataSheetStyles.calibrationColumn,
                        DataSheetStyles.calibrationValue,
                      ]}>
                      {step.distanceMeasurement}
                    </Text>
                    <Text
                      style={[
                        DataSheetStyles.calibrationColumn,
                        DataSheetStyles.calibrationValue,
                      ]}>
                      80% ±6dB
                    </Text>
                  </View>
                )
              })}
            </View>

            <View style={{ marginBottom: 8 }}>
              <Image src={toka5Image} style={{ maxWidth: '90%' }} />
            </View>
          </View>
        </View>

        <View style={{ marginBottom: 4 }}>
          <DataSheetTitle>Sensor Payload</DataSheetTitle>
          <View style={DataSheetStyles.twoColumnLayout}>
            <View style={DataSheetStyles.column}>
              <DataSheetListedItem
                fieldName='Payload Type:'
                fieldValue={payloadTypesStr}
              />
              <DataSheetListedItem
                fieldName='Sensor Frequency:'
                fieldValue={sensorFrequenciesStr}
              />
              <DataSheetListedItem
                fieldName='Sensor Diameter:'
                fieldValue={sensorDiametersStr}
              />
            </View>
            <DataSheetSeparator />
            <View style={DataSheetStyles.column}>
              <DataSheetListedItem
                fieldName='Angle:'
                fieldValue={
                  sensorAngle !== undefined ? `${sensorAngle}°` : undefined
                }
              />
              <DataSheetListedItem
                fieldName='Wave Mode:'
                fieldValue='Longitudinal'
              />
              <DataSheetListedItem
                fieldName='Couplant Type:'
                fieldValue='Water'
              />
            </View>
          </View>
        </View>

        {sensorPayloadSerialNumbers.map((serialNumbers, i) => (
          <PayloadSerialNumbers
            key={i}
            serialNumbers={serialNumbers}
            index={i + 1}
            isLast={i === sensorPayloadSerialNumbers.length - 1}
          />
        ))}
      </ReportPage>
      <AddendumPages
        sectionId={RugReportSectionId.DATA_SHEET}
        sectionTitle='Inspection Data Sheet'
      />
    </>
  )
}

type FormDataSheetSectionData = Omit<
  DataSheetSectionData,
  'calibrationBlocks' | 'calibrationTimes' | 'sensorSerialNumbers'
> & {
  calibrationBlocks: CalibrationBlock[]
  calibrationTimes: dayjs.Dayjs[]
  robots: Array<
    Omit<RobotData, 'dacs'> & {
      dacs?: FormDacData[]
    }
  >
  sensorSerialNumbers: PayloadSerialNumbers[]
}

type FormDacData = Omit<DacData, 'serialNumber' | 'calibrationDate'> & {
  serialNumber?: string
  calibrationDate?: dayjs.Dayjs | TimeString
}

export const getDacFormValues = (dacs?: DacData[]): FormDacData[] => {
  const dac = dacs?.[0]
  if (!dac) return [{ serialNumber: '' }]

  return [
    {
      ...dac,
      calibrationDate: dac.calibrationDate
        ? dayjs(dac.calibrationDate)
        : undefined,
    },
  ]
}

export const getDacDataFromForm = (
  dacs?: FormDacData[]
): DacData[] | undefined => {
  const dac = dacs?.[0]
  if (!dac) return undefined

  const serialNumber = dac.serialNumber?.trim() ?? ''
  const calibrationDate = dayjs.isDayjs(dac.calibrationDate)
    ? (dac.calibrationDate.toISOString() as TimeString)
    : dac.calibrationDate

  if (!serialNumber && !calibrationDate) return undefined

  return [{ serialNumber, calibrationDate }]
}

const hasDacFormValue = (dac: FormDacData): boolean =>
  Boolean(dac.serialNumber?.trim() || dac.calibrationDate)

export const normalizeSensorSerialNumbers = (
  values: PayloadSerialNumbers[] | undefined
): DataSheetSectionData['sensorSerialNumbers'] => {
  if (!Array.isArray(values)) return {}

  return values.reduce(
    (result, item, index) => {
      const serialNumbers = (item.serialNumbers || [])
        .map((serialNumber) => serialNumber.trim())
        .filter((serialNumber) => serialNumber.length > 0)

      if (serialNumbers.length > 0) {
        result[index] = serialNumbers
      }

      return result
    },
    {} as DataSheetSectionData['sensorSerialNumbers']
  )
}

function parseSerialNumbers(text: string) {
  const regex = /\b[UB][A-Z0-9]+\b/g
  const matches = text.match(regex) || []
  return matches
}

const RenderReact: React.FC = () => {
  const [form] = Form.useForm<FormDataSheetSectionData>()
  const {
    formData,
    handleDataSheetSectionFinish,
    inspections,
    fieldSources,
    dacDataByRobotSerial = {},
  } = useRugReportOps()

  const sourceFor = React.useCallback(
    (path: string) => fieldSources?.[path],
    [fieldSources]
  )
  const [parserModalVisible, setParserModalVisible] = React.useState(false)
  const [parserText, setParserText] = React.useState('')
  const [robotSerialsToFetch, setRobotSerialsToFetch] = React.useState<
    string[]
  >([])

  const { entries: fetchedDacComponents } = useRolodexEntries(
    robotSerialsToFetch.length > 0
      ? robotSerialsToFetch.map((serialNumber) => ({
          type: RolodexEntryType.SALESFORCE_RUG_COMPONENT,
          filters: [{ key: 'name', op: '=', value: serialNumber }],
        }))
      : undefined
  )
  const fetchedDacDataByRobotSerial = useMemo(
    () => getDacDataByRobotSerial(fetchedDacComponents),
    [fetchedDacComponents]
  )

  const handleRobotSerialBlur = React.useCallback(
    (index: number) => {
      const robot = form.getFieldValue(['robots', index])
      const robotSerial =
        typeof robot?.serialNumber === 'string' ? robot.serialNumber.trim() : ''
      const dacData = findDacDataForRobotSerial(
        dacDataByRobotSerial,
        robotSerial
      )
      if (!dacData) {
        if (robotSerial) {
          setRobotSerialsToFetch((serials) =>
            serials.includes(robotSerial) ? serials : [...serials, robotSerial]
          )
        }
        return
      }

      const robots = form.getFieldValue('robots') || []
      const currentRobot = robots[index]
      if (!currentRobot) return

      form.setFieldsValue({
        robots: robots.map(
          (
            item: FormDataSheetSectionData['robots'][number],
            itemIndex: number
          ) =>
            itemIndex === index
              ? {
                  ...item,
                  dacs: item.dacs?.some(hasDacFormValue)
                    ? item.dacs
                    : getDacFormValues(dacData),
                }
              : item
        ),
      })
    },
    [dacDataByRobotSerial, form]
  )

  React.useEffect(() => {
    const robots: FormDataSheetSectionData['robots'] =
      form.getFieldValue('robots') || []
    const robotsWithFetchedDacs = robots.map(
      (item: FormDataSheetSectionData['robots'][number]) => {
        const robotSerial = item.serialNumber?.trim()
        if (!robotSerial || item.dacs?.some(hasDacFormValue)) return item

        const dacData = findDacDataForRobotSerial(
          fetchedDacDataByRobotSerial,
          robotSerial
        )
        if (!dacData) return item

        return {
          ...item,
          dacs: getDacFormValues(dacData),
        }
      }
    )

    if (
      robotsWithFetchedDacs.some(
        (_item: unknown, index: number) =>
          robotsWithFetchedDacs[index] !== robots[index]
      )
    ) {
      form.setFieldsValue({ robots: robotsWithFetchedDacs })
    }
  }, [fetchedDacDataByRobotSerial, form])

  const inspectionType = useMemo(() => {
    const inspectionTypes =
      inspections
        ?.map((inspection) => inspection.data.inspection_type)
        .filter(
          (
            inspectionType
          ): inspectionType is NonNullable<typeof inspectionType> =>
            !!inspectionType
        ) ?? []

    if (inspectionTypes.includes('rug')) {
      return 'rug'
    }

    return inspectionTypes.at(0) ?? 'rug'
  }, [inspections])

  const initialValues = React.useMemo(() => {
    const sensorSerialNumbersArray = toPayloadSerialNumberRows(
      formData.dataSheetSectionData?.sensorSerialNumbers
    )

    const calibrationTimes = formData.dataSheetSectionData?.calibrationTimes
      ? formData.dataSheetSectionData.calibrationTimes.map((time) =>
          dayjs(time)
        )
      : []

    return {
      inspectionSurface: formData.dataSheetSectionData?.inspectionSurface,
      surfaceCondition: formData.dataSheetSectionData?.surfaceCondition,
      coatingPresent: formData.dataSheetSectionData?.coatingPresent ?? false,
      procedure:
        formData.dataSheetSectionData?.procedure ??
        METHOD_TO_PROCEDURE[inspectionType],
      procedureTitle:
        formData.dataSheetSectionData?.procedureTitle ??
        METHOD_TO_PROCEDURE_TITLE[inspectionType],
      inspectionSoftware:
        formData.dataSheetSectionData?.inspectionSoftware ?? 'Fulcrum',
      payloads:
        (formData.dataSheetSectionData?.payloads?.length ?? 0) > 0
          ? formData.dataSheetSectionData?.payloads
          : DEFAULT_PAYLOADS,
      calibrationBlocks: formData.dataSheetSectionData?.calibrationBlocks || [],
      calibrationTimes,
      calibrationSteps:
        (formData.dataSheetSectionData?.calibrationSteps?.length ?? 0) > 0
          ? formData.dataSheetSectionData?.calibrationSteps
          : DEFAULT_CALIBRATION_STEPS,
      robots:
        formData.dataSheetSectionData?.robots?.map((robot) => ({
          ...robot,
          dacs: getDacFormValues(robot.dacs),
        })) || [],
      sensorSerialNumbers: sensorSerialNumbersArray,
    }
  }, [formData.dataSheetSectionData, inspectionType])

  const handleParse = React.useCallback(() => {
    const serialNumbers = parseSerialNumbers(parserText)

    if (serialNumbers.length === 0) {
      Modal.warning({
        title: 'No Serial Numbers Found',
        content:
          'No serial numbers matching the pattern were found in the text.',
      })
      return
    }

    const currentSerialNumbers = form.getFieldValue('sensorSerialNumbers') || []

    const chunks: PayloadSerialNumbers[] = []
    for (
      let i = 0;
      i < serialNumbers.length;
      i += DEFAULT_SERIAL_NUMBERS_COUNT
    ) {
      const chunk = serialNumbers.slice(i, i + DEFAULT_SERIAL_NUMBERS_COUNT)
      const paddedChunk = [
        ...chunk,
        ...Array(DEFAULT_SERIAL_NUMBERS_COUNT - chunk.length).fill(''),
      ]
      chunks.push({ serialNumbers: paddedChunk })
    }

    form.setFieldsValue({
      sensorSerialNumbers: [...currentSerialNumbers, ...chunks],
    })

    setParserModalVisible(false)
    setParserText('')
  }, [parserText, form])

  const handleFinish = React.useCallback(
    async (values: FormDataSheetSectionData) => {
      const sensorSerialNumbers = normalizeSensorSerialNumbers(
        values.sensorSerialNumbers
      )

      const calibrationTimes: TimeString[] =
        values.calibrationTimes?.map((time) => {
          if (dayjs.isDayjs(time)) {
            return time.toISOString() as TimeString
          }
          return time
        }) || []

      const processedValues: DataSheetSectionData = {
        ...values,
        robots: values.robots.map((robot) => ({
          ...robot,
          dacs: getDacDataFromForm(robot.dacs),
        })),
        sensorSerialNumbers,
        calibrationBlocks: values.calibrationBlocks || [],
        calibrationTimes,
        payloads: values.payloads || [],
      }

      await handleDataSheetSectionFinish(processedValues)
    },
    [handleDataSheetSectionFinish]
  )

  return (
    <StyledForm
      form={form}
      layout='vertical'
      onFinish={handleFinish}
      initialValues={initialValues}>
      <Form.Item
        name='inspectionSurface'
        label={
          <Space>
            <span>Inspection Surface</span>
            <SourceIndicator
              form={form}
              name='inspectionSurface'
              source={sourceFor('dataSheetSectionData.inspectionSurface')}
              initialValue={form.getFieldValue('inspectionSurface')}
            />
          </Space>
        }
        rules={[
          { required: true, message: 'Please select inspection surface' },
        ]}>
        <AutoComplete
          {...autocompletePreviewDisabledProps}
          allowClear
          style={autocompleteStyle}
          options={Object.values(ReportInspectionSurface).map((status) => {
            return { value: status }
          })}
          placeholder='Select or Type Surface'
          filterOption={autocompleteFilterOptionWithoutExactMatch}
        />
      </Form.Item>

      <Form.Item
        name='surfaceCondition'
        label={
          <Space>
            <span>Surface Condition</span>
            <SourceIndicator
              form={form}
              name='surfaceCondition'
              source={sourceFor('dataSheetSectionData.surfaceCondition')}
              initialValue={form.getFieldValue('surfaceCondition')}
            />
          </Space>
        }
        rules={[
          { required: true, message: 'Please describe surface condition' },
        ]}>
        <AutoComplete
          {...autocompletePreviewDisabledProps}
          allowClear
          style={autocompleteStyle}
          options={Object.values(ReportSurfaceCondition).map((status) => {
            return { value: status }
          })}
          placeholder='Select or Type Condition'
          filterOption={autocompleteFilterOptionWithoutExactMatch}
        />
      </Form.Item>

      <Form.Item
        name='coatingPresent'
        label={
          <Space>
            <span>Coating Present</span>
            <SourceIndicator
              form={form}
              name='coatingPresent'
              source={sourceFor('dataSheetSectionData.coatingPresent')}
              initialValue={form.getFieldValue('coatingPresent')}
            />
          </Space>
        }
        rules={[{ required: true }]}>
        <Radio.Group>
          <Radio value={true}>Yes</Radio>
          <Radio value={false}>No</Radio>
        </Radio.Group>
      </Form.Item>

      <Form.Item
        name='procedure'
        label={
          <Space>
            <span>Procedure</span>
            <SourceIndicator
              form={form}
              name='procedure'
              source={sourceFor('dataSheetSectionData.procedure')}
              initialValue={form.getFieldValue('procedure')}
            />
          </Space>
        }>
        <Input placeholder='e.g., GR2-FOP-011-CM' />
      </Form.Item>

      <Form.Item name='procedureTitle' label='Procedure Title'>
        <Input placeholder='e.g., General Rapid Ultrasonic Gridding Using TOKA Robots' />
      </Form.Item>

      <Form.Item name='inspectionSoftware' label='Inspection Software'>
        <AutoComplete
          {...autocompletePreviewDisabledProps}
          options={INSPECTION_SOFTWARE_OPTIONS}
          placeholder='Select or Type Inspection Software'
          filterOption={autocompleteFilterOptionWithoutExactMatch}
        />
      </Form.Item>

      <Form.Item label='Sensor Payloads'>
        <Form.List name='payloads'>
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <PayloadGroupWrapper key={key}>
                  <PayloadCard direction='vertical'>
                    <PayloadCardHeader>
                      <PayloadCardTitle>Payload {name + 1}</PayloadCardTitle>
                      <RemoveButton onClick={() => remove(name)} />
                    </PayloadCardHeader>
                    <CompactFormItem
                      {...restField}
                      name={[name, 'payloadType']}
                      label='Payload Type'>
                      <AutoComplete
                        {...autocompletePreviewDisabledProps}
                        options={Object.values(SensorPayloadType).map(
                          (status) => {
                            return { value: status }
                          }
                        )}
                        placeholder='Select or Type Payload Type'
                        filterOption={autocompleteFilterOptionWithoutExactMatch}
                      />
                    </CompactFormItem>
                    <CompactFormItem
                      {...restField}
                      name={[name, 'sensorFrequency']}
                      label='Sensor Frequency (MHz)'>
                      <PayloadFrequencyInput
                        placeholder='e.g., 5, 7.5, 10'
                        min={0}
                        step={0.5}
                      />
                    </CompactFormItem>
                    <FlushFormItem label='Sensor Diameter'>
                      <MeasurementInput
                        valueName={[name, 'sensorDiameter', 'value']}
                        unitName={[name, 'sensorDiameter', 'unit']}
                        unitOptions={DISTANCE_UNIT_OPTIONS}
                        placeholder='e.g., 0.125'
                        min={0}
                        step={0.001}
                        precision={3}
                        defaultUnit={GeckoMeasurementUnit.Inches.id}
                      />
                    </FlushFormItem>
                  </PayloadCard>
                </PayloadGroupWrapper>
              ))}
              <Form.Item>
                <ActionButton
                  onClick={() =>
                    add({
                      sensorDiameter: {
                        unit: GeckoMeasurementUnit.Inches.id,
                      },
                    })
                  }>
                  + Add Payload
                </ActionButton>
              </Form.Item>
            </>
          )}
        </Form.List>
      </Form.Item>

      <Form.Item
        label={
          <Space>
            <span>Calibration Blocks</span>
            <SourceIndicator
              form={form}
              name='calibrationBlocks'
              source={sourceFor('dataSheetSectionData.calibrationBlocks')}
              initialValue={form.getFieldValue('calibrationBlocks')}
            />
          </Space>
        }>
        <Form.List name='calibrationBlocks'>
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <InlineFormRow key={key}>
                  <Form.Item {...restField} name={[name, 'type']} noStyle>
                    <AutoComplete
                      {...autocompletePreviewDisabledProps}
                      style={{ width: '17.5rem' }}
                      options={Object.values(CalibrationBlockType).map(
                        (status) => {
                          return { value: status }
                        }
                      )}
                      placeholder='Type'
                      filterOption={autocompleteFilterOptionWithoutExactMatch}
                    />
                  </Form.Item>
                  <Form.Item
                    {...restField}
                    name={[name, 'serialNumber']}
                    noStyle>
                    <CalibrationBlockSerialInput placeholder='Serial Number' />
                  </Form.Item>
                  <RemoveButtonWithPadding onClick={() => remove(name)} />
                </InlineFormRow>
              ))}
              <Form.Item>
                <ActionButton onClick={() => add()}>
                  + Add Calibration Block
                </ActionButton>
              </Form.Item>
            </>
          )}
        </Form.List>
      </Form.Item>

      <Form.Item
        label={
          <Space>
            <span>Calibration Times</span>
            <SourceIndicator
              form={form}
              name='calibrationTimes'
              source={sourceFor('dataSheetSectionData.calibrationTimes')}
              initialValue={form.getFieldValue('calibrationTimes')}
            />
          </Space>
        }>
        <Form.List name='calibrationTimes'>
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <CalibrationTimeRow key={`space-${key}`} align='baseline'>
                  <Form.Item {...restField} name={name} key={`form-${key}`}>
                    <DatePicker showTime={{ format: 'HH:mm' }} />
                  </Form.Item>
                  <CloseOutlined onClick={() => remove(name)} />
                </CalibrationTimeRow>
              ))}
              <Form.Item>
                <ActionButton onClick={() => add()}>
                  + Add Calibration Time
                </ActionButton>
              </Form.Item>
            </>
          )}
        </Form.List>
      </Form.Item>

      <Form.Item
        label={
          <Space>
            <span>Calibration Steps</span>
            <SourceIndicator
              form={form}
              name='calibrationSteps'
              source={sourceFor('dataSheetSectionData.calibrationSteps')}
              initialValue={form.getFieldValue('calibrationSteps')}
            />
          </Space>
        }>
        <Form.List name='calibrationSteps'>
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <InlineFormRow key={key}>
                  <Form.Item {...restField} name={[name, 'stepName']} noStyle>
                    <AutoComplete
                      {...autocompletePreviewDisabledProps}
                      style={{ width: '8.75rem' }}
                      placeholder='Step Name'
                      options={CALIBRATION_STEP_OPTIONS}
                      filterOption={autocompleteFilterOptionWithoutExactMatch}
                    />
                  </Form.Item>
                  <Form.Item
                    {...restField}
                    name={[name, 'distance', 'value']}
                    noStyle>
                    <DistanceValueInput placeholder='Value' precision={3} />
                  </Form.Item>
                  <Form.Item
                    {...restField}
                    name={[name, 'distance', 'unit']}
                    noStyle>
                    <UnitSelect
                      placeholder='Unit'
                      options={DISTANCE_UNIT_OPTIONS}
                    />
                  </Form.Item>
                  <RemoveIcon size={18} onClick={() => remove(name)} />
                </InlineFormRow>
              ))}
              <Form.Item>
                <ActionButton onClick={() => add()}>+ Add Step</ActionButton>
              </Form.Item>
            </>
          )}
        </Form.List>
      </Form.Item>

      <Form.Item
        label={
          <Space>
            <span>Robots</span>
            <SourceIndicator
              form={form}
              name='robots'
              source={sourceFor('dataSheetSectionData.robots')}
              initialValue={form.getFieldValue('robots')}
            />
          </Space>
        }>
        <Form.List name='robots'>
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <PayloadGroupWrapper key={key}>
                  <InlineFieldGroup>
                    <InlineFormItem
                      {...restField}
                      key={`model-${key}`}
                      name={[name, 'model']}>
                      <AutoComplete
                        {...autocompletePreviewDisabledProps}
                        options={ROBOT_MODEL_OPTIONS}
                        placeholder='Robot Model'
                        filterOption={autocompleteFilterOptionWithoutExactMatch}
                      />
                    </InlineFormItem>
                    <InlineFormItem
                      {...restField}
                      key={`serialNumber-${key}`}
                      name={[name, 'serialNumber']}>
                      <Input
                        placeholder='Robot/DAC S/N'
                        onBlur={() => handleRobotSerialBlur(name)}
                      />
                    </InlineFormItem>
                    <RobotDacFields>
                      <InlineFieldGroup>
                        <Form.Item
                          name={[name, 'dacs', 0, 'serialNumber']}
                          noStyle>
                          <RobotDacInput placeholder='DAC S/N' />
                        </Form.Item>
                        <Form.Item
                          name={[name, 'dacs', 0, 'calibrationDate']}
                          noStyle>
                          <RobotDacDatePicker
                            placeholder='DAC Calibration Due Date'
                            format='MM/DD/YYYY'
                          />
                        </Form.Item>
                      </InlineFieldGroup>
                    </RobotDacFields>
                    <RemoveButtonWithPadding onClick={() => remove(name)} />
                  </InlineFieldGroup>
                </PayloadGroupWrapper>
              ))}
              <Form.Item>
                <ActionButton
                  onClick={() =>
                    add({ model: 'toka_5', dacs: getDacFormValues() })
                  }>
                  + Add Robot
                </ActionButton>
              </Form.Item>
            </>
          )}
        </Form.List>
      </Form.Item>

      <Form.Item
        label={
          <Space>
            <span>Sensor Serial Numbers</span>
            <SourceIndicator
              form={form}
              name='sensorSerialNumbers'
              source={sourceFor('dataSheetSectionData.sensorSerialNumbers')}
              initialValue={form.getFieldValue('sensorSerialNumbers')}
            />
          </Space>
        }>
        <Form.List name='sensorSerialNumbers'>
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name }) => (
                <PayloadGroupWrapper key={key}>
                  <PayloadCard direction='vertical'>
                    <PayloadCardHeader>
                      <PayloadCardTitle>Robot {name + 1}</PayloadCardTitle>
                      <RemoveButton onClick={() => remove(name)} />
                    </PayloadCardHeader>
                    <Form.List name={[name, 'serialNumbers']}>
                      {(subFields, subOpt) => (
                        <VerticalStack>
                          {subFields.map(
                            ({ key, name, ...restField }, index) => (
                              <Space.Compact key={key}>
                                <SerialNumberLabel>
                                  {index + 1}.
                                </SerialNumberLabel>
                                <InlineFormItem {...restField} name={[name]}>
                                  <Input
                                    placeholder={`Serial Number ${index + 1}`}
                                  />
                                </InlineFormItem>
                                <RemoveButtonWithPadding
                                  onClick={() => subOpt.remove(name)}
                                />
                              </Space.Compact>
                            )
                          )}
                          <ActionButton onClick={() => subOpt.add()}>
                            + Add Serial Number
                          </ActionButton>
                        </VerticalStack>
                      )}
                    </Form.List>
                  </PayloadCard>
                </PayloadGroupWrapper>
              ))}
              <Form.Item>
                <VerticalStack>
                  <CantileverButton onClick={() => setParserModalVisible(true)}>
                    Open Parser
                  </CantileverButton>
                  <CantileverButton
                    onClick={() => {
                      add({
                        serialNumbers: Array(DEFAULT_SERIAL_NUMBERS_COUNT).fill(
                          ''
                        ),
                      })
                    }}>
                    + Add Robot Serial Numbers
                  </CantileverButton>
                </VerticalStack>
              </Form.Item>
            </>
          )}
        </Form.List>
      </Form.Item>

      <Form.Item>
        <ActionButton primary onClick={() => form.submit()}>
          Save Inspection Data Sheet
        </ActionButton>
      </Form.Item>

      <Modal
        title='Serial Number Parser'
        open={parserModalVisible}
        onCancel={() => {
          setParserModalVisible(false)
          setParserText('')
        }}
        footer={[
          <CantileverButton
            key='cancel'
            onClick={() => {
              setParserModalVisible(false)
              setParserText('')
            }}>
            Cancel
          </CantileverButton>,
          <CantileverButton key='parse' primary onClick={handleParse}>
            Parse
          </CantileverButton>,
        ]}
        width={800}>
        <ParserModalDescription>
          <p>
            Paste text containing serial numbers below. Serial numbers starting
            with &quot;U&quot; or &quot;B&quot; followed by letters and numbers
            will be extracted.
          </p>
        </ParserModalDescription>
        <ParserTextArea
          value={parserText}
          onChange={(e) => setParserText(e.target.value)}
          placeholder='Paste your text here...'
          rows={15}
        />
      </Modal>
    </StyledForm>
  )
}

export const InspectionDataSheetSection: ReportSection = {
  id: RugReportSectionId.DATA_SHEET,
  title: 'Inspection Data Sheet',
  description: 'Technical specifications of the inspection',
  required: true,
  RenderPDF: RenderPDF,
  RenderReact: RenderReact,
  requiredFields: [
    'dataSheetSectionData.inspectionSurface',
    'dataSheetSectionData.surfaceCondition',
    'dataSheetSectionData.coatingPresent',
    'dataSheetSectionData.calibrationBlocks',
  ],
}

interface PayloadSerialNumbersProps {
  serialNumbers: string[]
  index: number
  isLast: boolean
}

const PayloadSerialNumbers: React.FC<PayloadSerialNumbersProps> = ({
  serialNumbers,
  index,
  isLast,
}) => {
  const chunkLength = 6
  const chunkedSerialNumbers = chunk(serialNumbers, chunkLength)

  return (
    <View
      wrap={false}
      style={[
        DataSheetStyles.payloadContainer,
        isLast ? DataSheetStyles.payloadContainerLast : {},
      ]}>
      <View style={DataSheetStyles.payloadTitle}>
        <Text>Payload #{index}</Text>
      </View>

      <View style={DataSheetStyles.tableContainer}>
        {chunkedSerialNumbers.map((chunkArray, i) => (
          <View
            key={i}
            style={
              i === chunkedSerialNumbers.length - 1
                ? DataSheetStyles.tableColumn
                : DataSheetStyles.tableColumnWithBorder
            }>
            <View key={`header-${i}`} style={DataSheetStyles.tableHeaderRow}>
              <View style={DataSheetStyles.tableDataRow}>
                <Text style={DataSheetStyles.tableHeader}>Sensor #</Text>
                <Text style={DataSheetStyles.tableHeader}>Sensor S/N</Text>
              </View>
            </View>
            {chunkArray.map((serialNumber, j) => (
              <View
                key={`${index}-${i * chunkLength + j}`}
                style={DataSheetStyles.tableDataRow}>
                <Text style={DataSheetStyles.tableCell}>
                  {i * chunkLength + j + 1}
                </Text>
                <Text style={DataSheetStyles.tableCell}>{serialNumber}</Text>
              </View>
            ))}
          </View>
        ))}
      </View>
    </View>
  )
}
